/**
 * AuthProvider maintains authentication state for the application.
 *
 * It handles:
 * - login and logout flows
 * - refresh token session renewal
 * - localStorage persistence for auth state
 * - exposing auth status and user data to child components
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthRepository } from '../data/authRepository';
import { User } from '../data/userModel';
import { setTokenRenewer } from '../../../shared/api/post';
import { STORAGE_KEYS } from '../../../shared/constants';

const AuthContext = createContext({
  isAuthenticated: false,
  user: null,
  login: async () => {},
  refreshSession: async () => null,
  logout: async () => {},
  clearError: () => {},
  isLoading: false,
  error: null,
});

const AUTH_LOG_PREFIX = '[auth]';

const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? new Date(payload.exp * 1000).toISOString() : null;
  } catch (err) {
    return null;
  }
};

const getUserLogMeta = (user) => ({
  hasUser: Boolean(user),
  uid: user?.uid ?? null,
  employeeId: user?.employeeId ?? null,
  userName: user?.userName ?? null,
  companyId: user?.companyId ?? null,
  hasAccessToken: Boolean(user?.accessToken),
  hasRefreshToken: Boolean(user?.refreshToken),
  accessTokenExpiresAt: getTokenExpiry(user?.accessToken),
  refreshTokenExpiresAt: getTokenExpiry(user?.refreshToken),
});

const logAuth = (message, details = {}) => {
  console.log(AUTH_LOG_PREFIX, message, details);
};

const logAuthError = (message, error, details = {}) => {
  console.error(AUTH_LOG_PREFIX, message, {
    ...details,
    code: error?.code ?? null,
    message: error?.message ?? null,
  });
};

/**
 * Read the persisted authenticated user from localStorage.
 *
 * Returns null when no auth state is stored or parsing fails.
 */
const getStoredUser = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.authUser);
    logAuth('reading stored user', { hasStoredUser: Boolean(stored) });
    if (!stored) {
      return null;
    }

    const user = User.fromJson(JSON.parse(stored));
    logAuth('stored user restored, stored:', JSON.parse(stored));
    logAuth('stored user restored, user:', getUserLogMeta(user));
    return user;
  } catch (err) {
    logAuthError('failed to restore stored user', err);
    return null;
  }
};

/**
 * Persist the authenticated user object to localStorage.
 *
 * This enables session restoration after page refresh.
 */
const storeUser = (user) => {
  try {
    const json = typeof user?.toJson === 'function' ? user.toJson() : User.fromJson(user).toJson();
    localStorage.setItem(STORAGE_KEYS.authUser, JSON.stringify(json));
    logAuth('stored user updated', getUserLogMeta(user));
  } catch (err) {
    logAuthError('failed to store user', err);
  }
};

const mergeUser = (currentUser, nextUser) => {
  logAuth('merging refreshed user data', {
    current: getUserLogMeta(currentUser),
    incoming: getUserLogMeta(nextUser),
  });

  const merged = { ...currentUser };

  Object.entries(nextUser).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      merged[key] = value;
    }
  });

  return new User(merged);
};

const isSessionEnded = (error) =>
  ['INVALID_TOKEN', 'EXPIRED_TOKEN', 'TOKEN_EXPIRED', 'HTTP_401'].includes(error?.code);

/**
 * Wipe localStorage on logout or when the session becomes invalid.
 *
 * Everything the session produced goes, so nothing cached outlives it, but the
 * device preferences in STORAGE_KEYS.keepOnLogout stay: they belong to the
 * browser, not to whoever was signed in.
 */
const clearStoredUser = () => {
  try {
    const removedKeys = Object.keys(localStorage).filter((key) => !STORAGE_KEYS.keepOnLogout.includes(key));
    logAuth('clearing stored session keys', { removedKeys });
    Object.keys(localStorage)
      .filter((key) => !STORAGE_KEYS.keepOnLogout.includes(key))
      .forEach((key) => localStorage.removeItem(key));
  } catch (err) {
    logAuthError('failed to clear stored user', err);
  }
};

export const AuthProvider = ({ children }) => {
  const storedUser = getStoredUser();
  const [user, setUser] = useState(storedUser);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(storedUser));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    logAuth('provider state changed', {
      isAuthenticated,
      isLoading,
      hasError: Boolean(error),
      errorCode: error?.code ?? null,
      user: getUserLogMeta(user),
    });
  }, [isAuthenticated, isLoading, error, user]);

  /**
   * Authenticate the user with company, email and password.
   *
   * On success it updates auth state, persists the user and returns the normalized user object.
   * On failure it clears auth state and returns null.
   */
  const login = useCallback(async ({ company, email, password }) => {
    logAuth('login started', {
      company,
      email,
      hasPassword: Boolean(password),
    });
    setIsLoading(true);
    setError(null);

    try {
      const result = await AuthRepository.login({ company, email, password });
      logAuth('login succeeded', getUserLogMeta(result.user));
      setUser(result.user);
      setIsAuthenticated(true);
      storeUser(result.user);
      return result.user;
    } catch (err) {
      logAuthError('login failed', err, { company, email });
      setError(err);
      setIsAuthenticated(false);
      setUser(null);
      clearStoredUser();
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh the current session using the stored refresh token.
   *
   * When refresh succeeds, the updated user payload is merged into the current profile.
   * Temporary refresh failures keep the current profile instead of clearing the screen.
   */
  const refreshSession = useCallback(async () => {
    logAuth('refresh requested', getUserLogMeta(user));

    if (!user?.refreshToken) {
      logAuth('refresh skipped: no refresh token', getUserLogMeta(user));
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await AuthRepository.refresh({ refreshToken: user.refreshToken });
      logAuth('refresh succeeded', getUserLogMeta(result.user));
      const refreshedUser = mergeUser(user, result.user);
      setUser(refreshedUser);
      setIsAuthenticated(true);
      storeUser(refreshedUser);
      return refreshedUser;
    } catch (err) {
      logAuthError('refresh failed', err, {
        sessionEnded: isSessionEnded(err),
        user: getUserLogMeta(user),
      });

      if (!isSessionEnded(err)) {
        logAuth('refresh failed without ending session; keeping current user', getUserLogMeta(user));
        setError(null);
        setIsAuthenticated(Boolean(user));
        return null;
      }

      logAuth('refresh ended session; clearing auth state', getUserLogMeta(user));
      setError(null);
      setIsAuthenticated(false);
      setUser(null);
      clearStoredUser();
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Log the user out both locally and remotely.
   *
   * The backend logout call is attempted, but local auth state is cleared regardless.
   */
  const logout = useCallback(async () => {
    logAuth('logout started', getUserLogMeta(user));
    setIsLoading(true);
    setError(null);

    try {
      await AuthRepository.logout({ accessToken: user?.accessToken });
      logAuth('logout api succeeded');
    } catch (err) {
      // Still clear local state even if logout API fails.
      logAuthError('logout api failed; clearing local session anyway', err);
      setError(err);
    } finally {
      logAuth('logout clearing local auth state');
      setIsAuthenticated(false);
      setUser(null);
      clearStoredUser();
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Hand the API layer a way to renew an expired access token.
   *
   * Any 401 then refreshes and retries itself once. A refresh that fails clears
   * the session, and the router sends the user to the login page from there.
   */
  useEffect(() => {
    logAuth('token renewer registered');
    setTokenRenewer(async () => {
      logAuth('token renewer invoked');
      const refreshedUser = await refreshSession();
      logAuth('token renewer completed', {
        receivedAccessToken: Boolean(refreshedUser?.accessToken),
        user: getUserLogMeta(refreshedUser),
      });
      return refreshedUser?.accessToken ?? null;
    });
  }, [refreshSession]);

  /**
   * Clear any transient auth errors shown in the UI.
   */
  const clearError = useCallback(() => {
    logAuth('error cleared');
    setError(null);
  }, []);

  const value = useMemo(
    () => ({ isAuthenticated, user, login, refreshSession, logout, clearError, isLoading, error }),
    [isAuthenticated, user, login, refreshSession, logout, clearError, isLoading, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
